import { useTranslation } from 'react-i18next';
import { HOLD_ROLE_LIMITS } from '../board-grid/roles';
import type { BoardGridController } from '../board-grid/useBoardGrid';
import { DockedBar } from './DockedBar';
import { HoldBar } from './HoldBar';
import type { HoldHandControl } from './HoldHandButton';

/**
 * The bar of hold roles along the bottom of a board.
 *
 * The photo's bar (see `PhotoHoldBar`) over a grid: a tap places the hold and
 * picks it up, and what it *is* is said here — or said first, and then tapped.
 *
 * A board keeps the two limits a photo has not: one pair of starts, one pair of
 * finishes, refused on save. So the roles that have no slot left say why they
 * cannot be picked rather than quietly doing nothing — and a foot chip too small
 * to pull on says so too.
 *
 * The hand, where hands are forced, is the page's to give (`hand`): the hands
 * follow from the run the draft derives, which the grid only paints.
 */
export function BoardHoldBar({
  grid,
  hand,
  docked = true,
}: {
  grid: BoardGridController;
  hand?: HoldHandControl;
  /**
   * Over the bottom of the wall, or the bar alone, for whoever puts it beside
   * the wall instead (`ClimbEditorSplit`).
   */
  docked?: boolean;
}) {
  const { t } = useTranslation();
  const {
    selected,
    selectedRole,
    selectRole,
    deleteSelected,
    clearSelection,
    roleBlocked,
    circuit,
    selectedStep,
    moveSelectedStep,
    canMoveSelectedStep,
  } = grid;

  const bar = (
    <HoldBar
      testId="board-hold-command-bar"
      role={selectedRole}
      onRole={selectRole}
      onDismiss={clearSelection}
      onDelete={deleteSelected}
      canDelete={!!selected}
      deleteLabel={t('climbs.board.removeHold')}
      hand={hand}
      // Its place in the run, on a circuit: the top half of the button raises
      // the number, the bottom half lowers it. Dead while nothing in hand has a
      // place — no hold picked up, or a foot, which is no step of the run (see
      // circuit.ts).
      step={
        circuit
          ? {
              order: selectedStep,
              onIncrease: () => moveSelectedStep(1),
              onDecrease: () => moveSelectedStep(-1),
              canIncrease: canMoveSelectedStep(1),
              canDecrease: canMoveSelectedStep(-1),
            }
          : undefined
      }
      unavailable={(role) => {
        const blocked = roleBlocked(role);
        if (blocked === 'foot_only') return t('climbs.board.footOnlyHold');
        if (blocked === 'limit') {
          return t('climbs.board.roleLimitReached', {
            limit: HOLD_ROLE_LIMITS[role],
            role: t(`climbs.holdRoles.${role}.label`),
          });
        }

        return null;
      }}
    />
  );

  return docked ? <DockedBar>{bar}</DockedBar> : bar;
}
